'use client';

import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsInIcon, ArrowsOutIcon, ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';
import { MediaShell } from '@tranquility-ai/theme-components/media-shell';
import { Modal } from '@tranquility-ai/theme-components/modal';
import { Spinner } from '@tranquility-ai/theme-components/spinner';
import {
  UfdrViewer,
  type UfdrArtifactView,
  type UfdrChatSummary,
  type UfdrTreeNode,
} from '@tranquility-ai/theme-components/ufdr-viewer';
import { FeaturesContext } from '@/app/contexts';
import { useUfdrExportData, type UfdrPreviewContext } from '@/hooks/useUfdrExportData';
import { relatedFamily, useMobileForensicView } from '@/hooks/mobile-forensic/useMobileForensicView';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { isMobileAnalysisAgentEnabled } from '@/lib/mobile-analysis-agent';
import { MediaShellHeaderButton } from '@/components/ui/MediaShellHeaderButton';
import { UfdrMediaInline } from './UfdrMediaInline';
import { UfdrAssistantDock } from './ufdr-assistant-dock';

import '@tranquility-ai/theme-components/modal.css';
import '@tranquility-ai/theme-components/spinner.css';

export type EvidenceLockerMobileForensicOverlayProps = {
  isOpen: boolean;
  context: UfdrPreviewContext | null;
  onClose: () => void;
  /**
   * `modal` is the in-app dialog. `page` fills `/mobile-viewer` and only
   * offers close-tab, the same way the mailbox page does.
   */
  variant?: 'modal' | 'page';
  /** Still notified when a file is chosen. The file itself renders in the viewer. */
  onOpenMedia?: (url: string) => void;
  /** Page variant writes family, type, view, q, f, record, thread, and page into the URL. */
  onBrowseChange?: (patch: {
    family?: string;
    artifactType?: string;
    view?: string;
    search?: string;
    record?: string;
    threadId?: string;
    page?: string;
  }) => void;
};

function MobileForensicViewerFallback({ message }: { message: string }) {
  return (
    <div className="flex h-full min-h-[160px] items-center justify-center px-4 text-sm text-gray-600">
      {message}
    </div>
  );
}

function openUfdrInNewTab(context: UfdrPreviewContext) {
  const params = new URLSearchParams();
  if (context.url) params.set('url', context.url);
  if (context.path) params.set('path', context.path);
  if (context.caseFolderName) params.set('caseFolderName', context.caseFolderName);
  if (context.label) params.set('label', context.label);
  if (context.fileStatusId) params.set('fileStatusId', context.fileStatusId);
  if (context.acquisitionId != null) params.set('acquisitionId', String(context.acquisitionId));
  if (context.artifactType) params.set('type', context.artifactType);
  if (context.view) params.set('view', context.view);
  if (context.rowIds?.length) params.set('row', context.rowIds.join(','));
  if (context.threadId) params.set('threadId', context.threadId);
  if (context.family) params.set('family', context.family);
  if (context.search) params.set('q', context.search);
  if (context.filters) params.set('f', context.filters);
  if (context.record) params.set('record', context.record);
  if (context.page) params.set('page', context.page);
  if (typeof window !== 'undefined') {
    const paramsOnPage = new URLSearchParams(window.location.search);
    const caseId = paramsOnPage.get('case') || paramsOnPage.get('caseId');
    if (caseId) params.set('case', caseId);
  }
  window.open(`/mobile-viewer?${params.toString()}`, '_blank', 'noopener,noreferrer');
}

export function EvidenceLockerMobileForensicOverlay({
  isOpen,
  context,
  onClose,
  onOpenMedia,
  onBrowseChange,
  variant = 'modal',
}: EvidenceLockerMobileForensicOverlayProps) {
  const t = useTimePilotT();
  const features = useContext(FeaturesContext);
  const mobileEnabled = isMobileAnalysisAgentEnabled(features?.featureFlags ?? {});
  const query = useUfdrExportData(context, isOpen);
  const [openedMedia, setOpenedMedia] = useState<UfdrTreeNode | null>(null);
  const [expanded, setExpanded] = useState(false);
  const mobile = useMobileForensicView({
    enabled: isOpen && mobileEnabled,
    path: context?.path,
    caseFolderName: context?.caseFolderName,
    acquisitionId: context?.acquisitionId,
    artifactType: context?.artifactType,
    view: context?.view,
    family: context?.family,
    search: context?.search,
    filters: context?.filters,
    record: context?.record,
    threadId: context?.threadId,
    page: context?.page,
    rowIds: context?.rowIds,
    sidecar: query.data,
    title: context?.label,
  });
  const title =
    context?.label ||
    context?.fileInfo?.fileName ||
    context?.url.split('/').pop() ||
    t('mobileForensic.title');

  useEffect(() => {
    if (isOpen) return;
    setExpanded(false);
    setOpenedMedia(null);
  }, [isOpen]);

  useEffect(() => {
    setOpenedMedia(null);
  }, [context?.url, context?.fileStatusId, context?.artifactType]);

  useEffect(() => {
    if (!isOpen || variant === 'page') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, onClose, variant]);

  if (!isOpen || !context) return null;

  // A disabled export query stays `isPending` forever. Acquisition-only
  // links have no url, so that pending flag is not loading. Spin only while
  // an export can actually run, or while mobile-service is still resolving.
  const exportStillLoading = Boolean(context?.url) && query.isPending && query.data == null;
  const mobileStillLoading = Boolean(mobile.loading) && !mobile.matched && query.data == null;
  const fallback = exportStillLoading || mobileStillLoading
    ? (
      <div className="flex h-full min-h-[160px] items-center justify-center px-4">
        <Spinner size="lg" label={t('mobileForensic.loadingExport')} />
      </div>
    )
    : query.isError
      ? <MobileForensicViewerFallback message={t('mobileForensic.loadError')} />
      : <MobileForensicViewerFallback message={t('mobileForensic.emptyExport')} />;

  const controls = variant === 'page' ? (
    <MediaShellHeaderButton label={t('mobileForensic.closeTab')} onClick={onClose}>
      <XIcon size={16} />
    </MediaShellHeaderButton>
  ) : (
    <>
      <MediaShellHeaderButton
        label={expanded ? t('mobileForensic.collapse') : t('mobileForensic.expand')}
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? <ArrowsInIcon size={16} /> : <ArrowsOutIcon size={16} />}
      </MediaShellHeaderButton>
      <MediaShellHeaderButton label={t('mobileForensic.openTab')} onClick={() => openUfdrInNewTab(context)}>
        <ArrowSquareOutIcon size={16} />
      </MediaShellHeaderButton>
      <MediaShellHeaderButton label={t('mobileForensic.close')} onClick={onClose}>
        <XIcon size={16} />
      </MediaShellHeaderButton>
    </>
  );

  const viewerData = mobile.data ?? query.data ?? { tree: [] };
  const assistant = mobile.acquisitionId != null ? (
    <UfdrAssistantDock acquisitionId={mobile.acquisitionId} caseName={mobile.caseName ?? ''} />
  ) : undefined;
  const searchText = mobile.model?.search ?? mobile.workspace?.query ?? '';
  const body = query.data || mobile.matched ? (
    <UfdrViewer
      data={viewerData}
      compact={variant !== 'page'}
      loading={Boolean(mobile.loading)}
      section={mobile.matched ? 'artifacts' : undefined}
      artifactType={mobile.artifactType}
      view={mobile.view}
      activeNodeId={mobile.model?.activeNodeId}
      workspace={{
        ...mobile.workspace,
        ...(assistant ? { dock: assistant } : {}),
        query: searchText,
        onQueryChangeText: (text: string) => {
          mobile.onSearchChange(text);
          mobile.workspace?.onQueryChangeText?.(text);
          onBrowseChange?.({ search: text });
        },
      }}
      status={mobile.error ? { status: 'error', message: mobile.error } : undefined}
      onArtifactTypeChange={(typeId) => {
        if (mobile.matched) mobile.onArtifactTypeChange(typeId);
        else mobile.onTypeSelect(typeId);
        onBrowseChange?.({ artifactType: typeId, threadId: '', page: '1' });
      }}
      onViewChange={(view: UfdrArtifactView) => {
        mobile.onViewChange(view);
        onBrowseChange?.({ view });
      }}
      onQueryChange={(next) => {
        mobile.onQueryChange(next);
        onBrowseChange?.({ page: String(next.page) });
      }}
      onRowSelect={(rowId) => {
        mobile.onRecordSelect(rowId);
        onBrowseChange?.({ record: rowId, threadId: '' });
      }}
      onContactSelect={(contact) => {
        if (!contact) return;
        mobile.onRecordSelect(contact.id);
        onBrowseChange?.({ record: contact.id, threadId: '' });
      }}
      onChatSelect={(chat: UfdrChatSummary | undefined) => {
        const threadId = chat?.id;
        mobile.onThreadSelect(threadId);
        if (chat?.app) mobile.onRelatedSelect({ typeId: chat.app, id: chat.id });
        if (!mobile.matched) query.onChatSelect?.(chat ? { id: chat.id } : undefined);
        onBrowseChange?.(
          chat?.app
            ? {
                family: relatedFamily(chat.app),
                artifactType: chat.app,
                view: '',
                record: chat.id,
                threadId: chat.id,
                page: '1',
              }
            : { threadId: threadId ?? '' },
        );
      }}
      onNodeSelect={(node) => {
        mobile.onShowInFiles(node.id);
        onBrowseChange?.({ family: 'files', artifactType: '', record: '', threadId: '' });
      }}
      onOpenMedia={(item) => {
        const url = 'mediaSrc' in item ? item.mediaSrc : 'src' in item ? item.src : undefined;
        if (!url) return;
        onOpenMedia?.(url);
      }}
      renderMedia={(item) => <UfdrMediaInline item={item} />}
    />
  ) : (
    fallback
  );

  const framed = openedMedia ? (
    <div className="relative flex h-full min-h-0 flex-1 flex-col">
      {body}
      <div className="absolute inset-0 z-10 flex min-h-0 flex-col bg-white">
        <button
          type="button"
          className="px-3 py-2 text-left text-sm text-gray-700"
          onClick={() => setOpenedMedia(null)}
        >
          {t('mobileForensic.back')}
        </button>
        <div className="min-h-0 flex-1">
          <UfdrMediaInline item={openedMedia} />
        </div>
      </div>
    </div>
  ) : (
    body
  );

  if (variant === 'page') {
    return (
      <div className="flex h-screen w-full flex-col">
        <MediaShell
          mode="pane"
          title={title}
          headerActions={undefined}
          controls={controls}
          className="h-full min-h-0 w-full"
          bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden !p-0"
        >
          <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">{framed}</div>
        </MediaShell>
      </div>
    );
  }

  if (expanded && typeof document !== 'undefined') {
    return createPortal(
      <div className="fixed inset-0 z-[100000] flex bg-white">
        <MediaShell
          mode="pane"
          title={title}
          headerActions={undefined}
          controls={controls}
          className="h-full min-h-0 w-full"
          bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden !p-0"
        >
          <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">{framed}</div>
        </MediaShell>
      </div>,
      document.body,
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      size="xl"
      showCloseButton={false}
      dismissOnEscape={false}
      headerActions={undefined}
      controls={controls}
      contentClassName="tp-media-shell !h-[80vh] !min-h-[80vh] !max-h-[80vh]"
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden !p-0"
    >
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">{framed}</div>
    </Modal>
  );
}
